import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TouchableWithoutFeedback, Keyboard, Platform } from 'react-native';
import { RFValue } from 'react-native-responsive-fontsize';
import analytics from '@react-native-firebase/analytics';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from 'styled-components/native';
import {addInAppTrigger} from '../../../lib/pushMessaging';

import { Header } from '../../../components/Interface/Headers/Header';
import {
  Container,
  Content,
  ContentHeader,
  Regions,
  RegionContainer,
  RegionTitle,
  ArrowIcon,
  NotFoundContainer,
  RegionTitleContainer,
} from './styles';
import { SearchInput } from '../../../components/Interface/Forms/Inputs/SearchInput';
import { Titles } from '../../../components/Interface/Typographies/Titles';
import { NotFound } from '../../../components/Interface/Search/NotFound';
import { Loading } from '../../../components/Interface/Loadings/Loading';
import { Badge } from '../../../components/Interface/Badges/Badge';

import { useHelpers } from '../../../context/helpers';
import { useAuth } from '../../../context/auth';
import { api } from '../../../services/api';

import { ddds } from '../../../utils/ddds';
import { RegiaoDTO } from '../../../dtos/RegiaoDTO';

interface Props {
  tipo: string;
  oneCityAction: (item: RegiaoDTO) => void;
  manyCitiesAction: (item: RegiaoDTO) => void;
}

export function RegionsList({ tipo, oneCityAction, manyCitiesAction }: Props) {
  const { ordenaEstadosOrdemAlfabetica, formataNomeEstado } = useHelpers();
  const theme = useTheme();
  const { user } = useAuth();

  const [data, setData] = useState<RegiaoDTO[]>([]);
  const [allData, setAllData] = useState<RegiaoDTO[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [fetchedOnce, setFetchedOnce] = useState(false);
  const [dddUser, setDddUser] = useState('');

  useEffect(() => {
    if (Platform.OS !== 'web') {
      addInAppTrigger('screen', 'fuel_localization');
    }
  }, []);

  function normalizeText(value: string) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  function sortEstadosByDdd(estados: RegiaoDTO[], ddd: string) {
    if (!ddd) return ordenaEstadosOrdemAlfabetica([...estados]);
    return [...estados].sort((a: any, b: any) => {
      const dddEstadoA = Object.keys(ddds).find((key) => ddds[key] === a._id);
      const dddEstadoB = Object.keys(ddds).find((key) => ddds[key] === b._id);

      if (dddEstadoA === ddd) return -1;
      if (dddEstadoB === ddd) return 1;
      return String(a._id).localeCompare(String(b._id));
    });
  }

  async function searchRegions(name: string) {
    setSearch(name);

    analytics().logEvent('campo_busca_regiao', {
      search_term: name,
    });

    try {
      const termo = normalizeText(name.trim());
      if (!termo) {
        setData(sortEstadosByDdd(allData, dddUser));
        return;
      }

      const filtrado = allData.filter((estado) => {
        const sigla = normalizeText(String(estado?._id ?? ''));
        const nome = normalizeText(formataNomeEstado(String(estado?._id ?? '')));
        return sigla.includes(termo) || nome.includes(termo);
      });

      const ordenado = ordenaEstadosOrdemAlfabetica(filtrado);
      setData(ordenado);
    } catch (err) {
      console.log(err);
    } finally {
      setFetchedOnce(true); 
    }
  }

  function handleRegion(item: RegiaoDTO) {
    if (item.cidades.length === 1) {
      oneCityAction(item);
    } else {
      manyCitiesAction(item);
    }
  }

  useFocusEffect(
    useCallback(() => {
      let isMounted = true;

      analytics().logEvent('tela_escolher_estado_tipo_' + tipo);

      async function fetchRegions() {
        try {
          const response = await api.get(`/listaRegioes?tipo=${tipo}`);
          if (!isMounted) return;

          const payload = (response?.data ?? []) as RegiaoDTO[];

          const dddUserRaw = String(user?.phone ?? '').replace(/\D/g, '');
          // Tratar números com +55 e também números locais
          const dddUser =
            dddUserRaw.startsWith('55') && dddUserRaw.length >= 4
              ? dddUserRaw.substring(2, 4)
              : dddUserRaw.substring(0, 2);
          setDddUser(dddUser);

          const estadosComCidades = payload.filter(
            (estado: RegiaoDTO) => Array.isArray(estado.cidades) && estado.cidades.length > 0
          );

          setAllData(estadosComCidades);

          const estadosOrdenados = sortEstadosByDdd(estadosComCidades, dddUser);
          setData(estadosOrdenados);
        } catch (err) {
          console.log(err);
        } finally {
          if (isMounted) {
            setIsLoading(false);
            setFetchedOnce(true);
          }
        }
      }

      fetchRegions();
      return () => {
        isMounted = false;
      };
    }, [tipo, user?.phone])
  );

  /**
   * Regra "Em Breve":
   * Pelo payload que você enviou, faz sentido considerar "Em Breve" quando:
   * - o estado tem APENAS UMA cidade e
   * - ALGUMA das regiões dessa cidade tem `breve: true`.
   *
   * Isso é equivalente ao que você fazia, mas sem precisar montar um array auxiliar.
   */
  const isEmBreve = useCallback((item: RegiaoDTO) => {
    if (!item?.cidades || item.cidades.length !== 1) return false;
    const [cidade] = item.cidades;
    return Array.isArray(cidade?.regioes) && cidade.regioes.some((r) => r?.breve === true);
  }, []);

  // Mostrar NotFound apenas depois que já houve ao menos um fetch/busca
  const shouldShowNotFound = useMemo(() => {
    if (!fetchedOnce) return false;
    return data.length === 0;
  }, [fetchedOnce, data.length]);

  return isLoading ? (
    <Loading />
  ) : (
    <TouchableWithoutFeedback
      onPress={() => {
        if (Platform.OS !== 'web') Keyboard.dismiss();
      }}
      accessible={false}
    >
      <Container>
        <Header icon="chevron-left" />
        <Content>
          <ContentHeader>
            <Titles title="Qual estado?" />
            <SearchInput
              placeholder="Buscar estado"
              onChangeText={(text) => searchRegions(text)}
              value={search}
            />
          </ContentHeader>

          {shouldShowNotFound ? (
            <NotFoundContainer>
              <NotFound style={{ marginTop: -RFValue(40) }} />
            </NotFoundContainer>
          ) : (
            <Regions
              contentContainerStyle={{ paddingBottom: RFValue(20) }}
              showsVerticalScrollIndicator={false}
            >
              {data.map((item: RegiaoDTO) => {
                const breve = isEmBreve(item);
                return (
                  <RegionContainer
                    key={item._id}
                    underlayColor={!breve ? theme.colors.button_click_bg : undefined}
                    disabled={breve}
                    onPress={() => {
                      if (!breve) handleRegion(item);
                    }}
                  >
                    <>
                      <RegionTitleContainer>
                        <RegionTitle>{formataNomeEstado(item._id)}</RegionTitle>
                        {breve && (
                          <Badge
                            text="Em Breve"
                            textStyle={{ color: theme.colors.shape }}
                            containerStyle={{
                              backgroundColor: theme.colors.blue,
                              borderColor: theme.colors.blue,
                              marginLeft: RFValue(6),
                            }}
                          />
                        )}
                      </RegionTitleContainer>
                      {!breve && <ArrowIcon name="chevron-right" />}
                    </>
                  </RegionContainer>
                );
              })}
            </Regions>
          )}
        </Content>
      </Container>
    </TouchableWithoutFeedback>
  );
}
